import React, { useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { PasswordField } from "@/components/PasswordField";
import { PasswordInput } from "@/components/PasswordInput";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import Navbar from "@/components/Navbar";


export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [pwValid, setPwValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const match = password.length > 0 && password === confirm;

  const onPwValidity = useCallback((v) => setPwValid(v), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwValid || !match) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-foreground" data-testid="reset-page">
      <Navbar />
      <div className="flex items-center justify-center px-6 py-10 min-h-[calc(100vh-4rem)]">
      <div className="w-full max-w-sm animate-fade-up">

        {!token ? (
          <div data-testid="reset-invalid" className="border border-blaze/50 bg-blaze/10 p-5">
            <AlertTriangle className="w-7 h-7 text-blaze" />
            <p className="font-heading text-xl font-bold text-foreground mt-3">Reset link missing</p>
            <p className="text-sm text-secondary-foreground mt-2 font-body">This page needs a valid reset link. Request a new one below.</p>
            <Link to="/forgot-password" className="mt-4 inline-flex w-full items-center justify-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">
              Request a new link
            </Link>
          </div>
        ) : done ? (
          <div data-testid="reset-success" className="border border-olive/40 bg-olive/5 p-5">
            <CheckCircle2 className="w-7 h-7 text-olive-bright" />
            <p className="font-heading text-2xl font-bold text-foreground mt-3">Password set.</p>
            <p className="text-sm text-secondary-foreground mt-2 font-body leading-relaxed">
              Your new password is active and every other session has been signed out. Log in with your email and new password.
            </p>
            <Link to="/login" data-testid="reset-login-link" className="mt-4 inline-flex w-full items-center justify-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm py-2.5 transition-colors">
              Log In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Set a New Password</h1>
            <p className="text-sm text-secondary-foreground mt-1 font-body">Choose a strong password. This link can only be used once.</p>
            <form onSubmit={submit} className="space-y-4 mt-6">
              {error && (
                <div data-testid="reset-error" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">
                  {error}
                  {/expired|invalid/i.test(error) && <> <Link to="/forgot-password" className="underline">Request a new link</Link>.</>}
                </div>
              )}
              <PasswordField
                id="reset-password"
                testId="reset-password"
                label="New Password"
                value={password}
                onChange={setPassword}
                onValidityChange={onPwValidity}
              />
              <div>
                <label htmlFor="reset-confirm" className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">Confirm New Password</label>
                <div className="mt-1">
                  <PasswordInput id="reset-confirm" testId="reset-confirm" inputTestId="reset-confirm" toggleTestId="reset-confirm-toggle"
                    value={confirm} onChange={(v) => { setConfirm(v); if (!confirmTouched) setConfirmTouched(true); }} autoComplete="new-password" />
                </div>
                {confirmTouched && confirm.length > 0 && !match && (
                  <p data-testid="reset-confirm-mismatch" className="mt-1.5 text-xs font-mono text-blaze">Passwords don't match.</p>
                )}
                {match && (
                  <p data-testid="reset-confirm-match" className="mt-1.5 text-xs font-mono text-olive-bright">Passwords match.</p>
                )}
              </div>
              <button data-testid="reset-submit" disabled={!pwValid || !match || busy}
                className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold uppercase text-base py-3 transition-colors">
                {busy ? "Saving..." : "Set Password"}
              </button>
            </form>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
